/**
 * @fileoverview Reassign-target mutation hook.
 * @description Wraps PATCH /sales/targets/:id/reassign in a TanStack mutation, invalidating both
 * the owning tenant's target query and its tenant queries (ownership/history both change) on
 * success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { reassignTarget } from '@/features/sales/services/targets.service'

import type { ReassignTargetRequest } from '@/features/sales/services/targets.service'

/**
 * @description Transfers a target's ownership (within the acting user's permitted hierarchy scope;
 * see reassignTarget's own doc comment) and refreshes the affected queries.
 * @param {string} tenantId - The tenant the target belongs to, for query invalidation.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof reassignTarget>>, unknown, {
 * targetId: string; input: ReassignTargetRequest }>>} The mutation object (mutate with `{ targetId,
 * input }`, isPending, ...).
 */
export function useReassignTarget(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetId, input }: { targetId: string; input: ReassignTargetRequest }) =>
      reassignTarget(targetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId] })
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
  })
}
